import { useEffect, useRef, useState } from "react";
import type { Shader } from "../types";
import { ShaderRunner } from "../lib/ShaderRunner";
import { GlslEditor } from "./GlslEditor";

type Props = {
  entry: Shader | null;
  onClose: () => void;
};

export function Detail({ entry, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runnerRef = useRef<ShaderRunner | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!entry || !canvasRef.current) return;
    const runner = new ShaderRunner(canvasRef.current, entry.frag);
    runnerRef.current = runner;
    runner.start();
    return () => {
      runner.dispose();
      runnerRef.current = null;
    };
  }, [entry]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (!entry) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(entry.frag);
      setCopied(true);
    } catch {
      /* clipboard blocked — silently no-op */
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="overlay__panel" onClick={(e) => e.stopPropagation()}>
        <header className="overlay__bar">
          <div className="overlay__crumbs">
            <span>shader-brew</span>
            <span className="overlay__sep">/</span>
            <span>w{String(entry.week).padStart(2, "0")}</span>
            <span className="overlay__sep">/</span>
            <span className="overlay__active">{entry.id}</span>
          </div>
          <button className="overlay__close" onClick={onClose}>
            esc ×
          </button>
        </header>

        <div className="overlay__body">
          <section className="overlay__stage">
            <canvas ref={canvasRef} className="overlay__canvas" />
            <div className="overlay__hud">
              <span>
                <span className="dot" />
                live · webgl
              </span>
              <span>{entry.date}</span>
            </div>
          </section>

          <aside className="overlay__side">
            <h2 className="overlay__title">{entry.title}</h2>
            <div className="overlay__meta">
              <span>w{String(entry.week).padStart(2, "0")}</span>
              <span className="overlay__sep">·</span>
              <span>{entry.tags.join(" · ")}</span>
            </div>
            {entry.notes.map((p, i) => (
              <p key={i} className="overlay__note">
                {p}
              </p>
            ))}

            <div className="code">
              <div className="code__bar">
                <span>shader.frag</span>
                <button className="lnk" onClick={onCopy}>
                  {copied ? "copied ✓" : "copy"}
                </button>
              </div>
              <GlslEditor value={entry.frag} readOnly minHeight="0" maxHeight="360px" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
